/**
 * Mock Data for User/Contractor Profiles
 * Used for development and testing
 */

import type { ContractorProfile } from '@hummii/shared-types';
import type { ProfileReview } from '../types';

export const MOCK_CONTRACTOR_PROFILE: ContractorProfile = {
  // Contractor fields
  id: 'contractor-1',
  userId: 'user-1',
  bio: "Hi, I'm John! I just opened my own company and decided to work for myself. I've been in the plumbing industry for around a decade, and for the past four years, I had the opportunity to work with a couple of big plumbing companies in the USA. My main aim is to ensure that people are delighted to collaborate with me and truly appreciate the excellent quality of my work. I providing all specter of residential and commercial plumbing.",
  experience: 10,
  hourlyRate: 85,
  businessName: 'John Smith Plumbing',
  latitude: 43.6532,
  longitude: -79.3832,
  address: '123 Main Street',
  city: 'Toronto',
  province: 'ON',
  postalCode: '81000',
  serviceRadius: 50,
  verificationStatus: 'APPROVED' as const,
  verificationDate: new Date('2023-01-15'),
  totalOrders: 50,
  completedOrders: 43,
  rating: 4.9,
  reviewCount: 23,
  isAvailable: true,
  createdAt: new Date('2023-01-01'),
  updatedAt: new Date('2024-12-19'),

  // User data
  user: {
    id: 'user-1',
    name: 'John Smith',
    avatarUrl: 'https://i.pravatar.cc/300?img=12',
    isVerified: true,
    roles: ['CONTRACTOR'],
  },

  // Categories
  categories: [
    {
      id: 'cat-contractor-1',
      category: {
        id: 'cat-1',
        nameEn: 'Plumbing',
        nameFr: 'Plomberie',
        slug: 'plumbing',
      },
    },
    {
      id: 'cat-contractor-2',
      category: {
        id: 'cat-2',
        nameEn: 'Heating',
        nameFr: 'Chauffage',
        slug: 'heating',
      },
    },
  ],

  // Portfolio
  portfolio: [
    {
      id: 'portfolio-1',
      title: 'Kitchen Renovation - Complete Plumbing',
      description: 'Complete plumbing installation for a modern kitchen renovation including sink, dishwasher, and garbage disposal.',
      imageUrl: 'https://images.unsplash.com/photo-1585128723941-25f2d9b92f05?w=800',
      completedAt: new Date('2024-11-15'),
      order: 1,
    },
    {
      id: 'portfolio-2',
      title: 'Bathroom Remodel',
      description: 'Full bathroom plumbing upgrade with new fixtures and piping.',
      imageUrl: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
      completedAt: new Date('2024-10-20'),
      order: 2,
    },
    {
      id: 'portfolio-3',
      title: 'Water Heater Installation',
      description: 'Installed new tankless water heater system for residential home.',
      imageUrl: 'https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=800',
      completedAt: new Date('2024-09-05'),
      order: 3,
    },
  ],

  // Rating distribution (matches reviewCount: 23, rating: 4.9)
  ratingDistribution: {
    fiveStars: 22,
    fourStars: 1,
    threeStars: 0,
    twoStars: 0,
    oneStar: 0,
  },
};

export const MOCK_REVIEWS: ProfileReview[] = [
  {
    id: 'review-1',
    rating: 5,
    comment: 'Excellent work! John was professional, punctual, and did an amazing job on our kitchen plumbing. Highly recommend!',
    criteriaRatings: {
      quality: 5,
      communication: 5,
      timeliness: 5,
      professionalism: 5,
    },
    isEdited: false,
    createdAt: new Date('2024-11-20'),
    reviewer: {
      id: 'user-2',
      name: 'Sarah Johnson',
      avatarUrl: 'https://i.pravatar.cc/300?img=5',
      isVerified: true,
    },
    response: {
      id: 'response-1',
      content: 'Thank you so much for your kind words! It was a pleasure working with you.',
      createdAt: new Date('2024-11-21'),
    },
  },
  {
    id: 'review-2',
    rating: 5,
    comment: 'Very satisfied with the bathroom remodel. John explained everything clearly and the work was completed on time.',
    criteriaRatings: {
      quality: 5,
      communication: 5,
      timeliness: 5,
      professionalism: 5,
    },
    isEdited: false,
    createdAt: new Date('2024-10-25'),
    reviewer: {
      id: 'user-3',
      name: 'Michael Chen',
      avatarUrl: 'https://i.pravatar.cc/300?img=8',
      isVerified: true,
    },
    response: null,
  },
  {
    id: 'review-3',
    rating: 4,
    comment: 'Great service overall. The water heater works perfectly. Only minor issue was the cleanup could have been better.',
    criteriaRatings: {
      quality: 5,
      communication: 4,
      timeliness: 4,
      professionalism: 4,
    },
    isEdited: false,
    createdAt: new Date('2024-09-10'),
    reviewer: {
      id: 'user-4',
      name: 'Emily Davis',
      avatarUrl: 'https://i.pravatar.cc/300?img=9',
      isVerified: false,
    },
    response: {
      id: 'response-2',
      content: 'Thank you for your feedback! I appreciate your comments about the cleanup - I will make sure to improve this in the future.',
      createdAt: new Date('2024-09-11'),
    },
  },
  {
    id: 'review-4',
    rating: 5,
    comment: null,
    criteriaRatings: {
      quality: 5,
      communication: 5,
      timeliness: 5,
      professionalism: 5,
    },
    isEdited: false,
    createdAt: new Date('2024-08-15'),
    reviewer: {
      id: 'user-5',
      name: 'David Wilson',
      avatarUrl: 'https://i.pravatar.cc/300?img=11',
      isVerified: true,
    },
    response: null,
  },
];
